import _ from "lodash";
import React, { useState, useRef, useContext } from "react";
import classes from "./TOKObject.scss";
import { setToastMsg } from "routes/modules/LoginModule";
import { useI18n, useHandleRightPane } from "Hooks";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import UIModal from "UIComponents/UIModal";
import ImageDropzoneV2 from "UIComponents/ImageDropzoneV2";
import { FormTextInput } from "UIComponents/InputTextField";
import { FormTextAreaInput } from "UIComponents/TextAreaInput";
import I18nHOC from "UIComponents/I18nHOC";
import MediaModal from "UIComponents/MediaModal";
import {
  AddOutlined,
  DeleteOutlined,
  PencilLineOutlined,
} from "@toddle-edu/ds-icons";
import {
  Button,
  IconButton,
  Tooltip,
  Dialog,
  PageHeader,
} from "@toddle-edu/ds-web";
import classNames from "classnames";
import { generateRandomId } from "Utils";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import HelperPane from "IBPlanner/routes/PlannerStep/components/HelperPane";
import { ProjectPlannerContext } from "Projects/modules/ProjectModules";
import LabelComponent from "Projects/components/StepContainer/components/LabelComponent";
import { SHIFT_ENTER_SUBMIT_SHORTCUT } from "Constants/stringConstants";

const RIGHT_PANE_ANIMATION_TIME = 300;

const DIALOGS_INFO = {
  DELETE: {
    title: "common:delete_with_label",
    label: "common:object",
    message: "common:delete_msg_with_label",
    confirmButtonText: "common:delete",
    cancelButtonText: "common:cancel",
  },
};

const styles = {
  imageStyles: {
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
  },
  imageContainer: {
    marginBottom: "0px",
    gap: "8px",
  },
  textInput: {
    gap: "0",
  },
};

const ModalComponent = props => {
  const {
    value,
    modalTitleLabel,
    modalTitleEditLabel,
    onClose,
    updateObjectList,
    setToastMsg,
    testId,
  } = props;
  const { t } = useI18n(["common", "project"]);
  const [objectDetails, setObjectDetails] = useState(() => {
    if (value) {
      return {
        id: value.id,
        imageUrl: value.imageUrl,
        title: value.title,
        source: value.source,
        remark: value.remark,
      };
    } else {
      return {
        id: generateRandomId(),
        imageUrl: "",
        title: "",
        source: "",
        remark: "",
      };
    }
  });

  const {
    currentRightPane,
    fieldId,
    onCloseRightPane,
    handleRightPane,
    showRightPane,
  } = useHandleRightPane({
    fieldId: "",
    currentRightPane: "",
    showRightPane: false,
    rightPaneAnimationTime: RIGHT_PANE_ANIMATION_TIME,
  });

  const {
    template: { body: { field_list = {} } = {}, helpEnabledFields = [] },
  } = useContext(ProjectPlannerContext);

  const titleRef = useRef({});
  const imageRef = useRef({});

  const updateObjectDetails = params => {
    setObjectDetails(prevState => ({ ...prevState, ...params }));
  };

  const onAddClick = () => {
    const titleError = titleRef.current.isValid();
    const imageError = imageRef.current.isValid();
    if (imageError) {
      setToastMsg({
        msg: "common:please_upload_a_image",
        type: "warning",
      });
    }

    const errorCount = titleError + imageError;

    if (errorCount === 0) {
      updateObjectList(objectDetails);
      onClose();
    }
  };

  const getIsHelpButtonActive = ({ uid }) => {
    return _.isEqual(currentRightPane, "help") && _.isEqual(fieldId, uid);
  };

  const setHelpText = ({ uid }) => {
    handleRightPane({
      rightPane: "help",
      id: uid,
    });
  };

  const getHelpTextDetails = ({ helpTextUid }) => {
    return _.find(helpEnabledFields, { uid: helpTextUid });
  };

  const rightPaneClasses = classNames(
    { [classes.rightPane]: true },
    { [classes.visible]: showRightPane },
    { [classes.hidden]: !showRightPane }
  );

  const getRightPaneComponent = () => {
    switch (currentRightPane) {
      case "help":
        return (
          <HelperPane
            onClose={onCloseRightPane}
            fieldObj={field_list[fieldId]}
            helperTextId={_.get(
              getHelpTextDetails({ helpTextUid: fieldId }),
              "helpText.id",
              null
            )}
            dsVersion="2.0"
          />
        );
      default:
        return null;
    }
  };

  return (
    <UIModal isOpen={true} isFullScreen={true}>
      <div className={classes.modalContainer}>
        <PageHeader>
          <PageHeader.Left
            title={value ? modalTitleEditLabel : modalTitleLabel}
            onBackClick={onClose}
            testId={testId}
          />
        </PageHeader>
        <div className={classes.scrollContainer}>
          <div className={classes.innerContainer}>
            <ImageDropzoneV2
              labelComponent={
                <div className="text-label">{t("common:image")}</div>
              }
              isRequired={true}
              customRef={ref => (imageRef.current = ref)}
              showUnsplash={false}
              dropMessage={t("common:dropzone_empty_msg")}
              name={"imageUrl"}
              containerStyle={styles.imageContainer}
              imageStyle={styles.imageStyles}
              value={objectDetails.imageUrl}
              updateInputField={updateObjectDetails}
              testId={testId}
              dsVersion="2.0"
            />
            <div className={classes.titleInputField}>
              <FormTextInput
                dsVersion="2.0"
                isRequired={true}
                placeholder={t("project:object_title_placeholder")}
                customRef={ref => (titleRef.current = ref)}
                name={"title"}
                value={objectDetails.title}
                updateInputField={updateObjectDetails}
                size="medium"
                labelComponent={
                  <LabelComponent
                    label={_.get(
                      field_list["TOKObjectsTitle"],
                      "config.label",
                      ""
                    )}
                    subText={_.get(
                      field_list["TOKObjectsTitle"],
                      "config.subText",
                      ""
                    )}
                    showHelper={
                      !_.isEmpty(
                        getHelpTextDetails({ helpTextUid: "TOKObjectsTitle" })
                      )
                    }
                    onHelpClick={() => setHelpText({ uid: "TOKObjectsTitle" })}
                    isHelpActive={getIsHelpButtonActive({
                      uid: "TOKObjectsTitle",
                    })}
                    isFieldHeader
                  />
                }
                testId={`${testId}-title`}
                containerStyle={styles.textInput}
              />
            </div>
            <div className={classes.inputField}>
              <FormTextInput
                dsVersion="2.0"
                placeholder={t("project:object_source_placeholder")}
                name={"source"}
                value={objectDetails.source}
                updateInputField={updateObjectDetails}
                size="medium"
                labelComponent={
                  <LabelComponent
                    label={_.get(
                      field_list["TOKObjectsSource"],
                      "config.label",
                      ""
                    )}
                    subText={_.get(
                      field_list["TOKObjectsSource"],
                      "config.subText",
                      ""
                    )}
                    showHelper={
                      !_.isEmpty(
                        getHelpTextDetails({ helpTextUid: "TOKObjectsSource" })
                      )
                    }
                    onHelpClick={() => setHelpText({ uid: "TOKObjectsSource" })}
                    isHelpActive={getIsHelpButtonActive({
                      uid: "TOKObjectsSource",
                    })}
                    isFieldHeader
                  />
                }
                testId={`${testId}-source`}
                containerStyle={styles.textInput}
              />
            </div>
            <div className={classes.inputField}>
              <FormTextAreaInput
                dsVersion="2.0"
                name={"remark"}
                placeholder={t("project:object_remark_placeholder")}
                minRows={2}
                value={objectDetails.remark}
                updateInputField={updateObjectDetails}
                labelComponent={
                  <LabelComponent
                    label={_.get(
                      field_list["TOKObjectsRemark"],
                      "config.label",
                      ""
                    )}
                    subText={_.get(
                      field_list["TOKObjectsRemark"],
                      "config.subText",
                      ""
                    )}
                    showHelper={
                      !_.isEmpty(
                        getHelpTextDetails({ helpTextUid: "TOKObjectsRemark" })
                      )
                    }
                    onHelpClick={() => setHelpText({ uid: "TOKObjectsRemark" })}
                    isHelpActive={getIsHelpButtonActive({
                      uid: "TOKObjectsRemark",
                    })}
                    isFieldHeader
                  />
                }
                testId={`${testId}-remark`}
                containerStyle={styles.textInput}
              />
            </div>
          </div>
        </div>
        <div className={rightPaneClasses}>{getRightPaneComponent()}</div>
      </div>
      <footer className={classes.footerContainer}>
        <div className={classes.innerFooterContainer}>
          <Button
            onClick={onClose}
            variant="neutral"
            type="outlined"
            dsVersion="2.0"
            size="medium"
            testId={`${testId}-cancel`}
          >
            {t(value ? "common:cancel" : "common:discard")}
          </Button>
          <Button
            onClick={onAddClick}
            variant="primary"
            type="fill"
            dsVersion="2.0"
            size="medium"
            shortcut={SHIFT_ENTER_SUBMIT_SHORTCUT}
            testId={`${testId}-save`}
          >
            {t(value ? "common:save" : "common:add")}
          </Button>
        </div>
      </footer>
    </UIModal>
  );
};

class TOKObject extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      showModal: false,
      showMediaModal: false,
      currentDialogAction: "",
      currentObjectDetails: null,
    };
  }

  toggleModal = () => {
    const { showModal } = this.state;
    if (showModal) {
      this.setState({ showModal: !showModal, currentObjectDetails: null });
    } else {
      this.setState({ showModal: !showModal });
    }
  };
  updateObjectList = params => {
    const { value } = this.props;
    const newList = [
      params,
      ..._.filter(value || [], item => item.id !== params.id),
    ];
    this.updateValue(newList);
  };

  onDelete = () => {
    const { value } = this.props;
    const { currentObjectDetails } = this.state;
    this.updateValue(
      _.filter(value || [], item => item.id !== currentObjectDetails.id)
    );

    this.closeDialogue();
  };

  closeDialogue = () => {
    this.setState({ currentDialogAction: "", currentObjectDetails: null });
  };

  onDeleteClick = item => {
    this.setState({
      currentObjectDetails: item,
      currentDialogAction: "DELETE",
    });
  };

  onEditClick = item => {
    this.setState({ currentObjectDetails: item });
    this.toggleModal();
  };

  toggleMediaModal = value => {
    this.setState({ showMediaModal: value });
    if (!value) {
      this.setState({ currentObjectDetails: null });
    }
  };

  showMediaModal = item => {
    this.setState({ currentObjectDetails: item });
    this.toggleMediaModal(true);
  };

  renderLabel = () => {
    const {
      value,
      label,
      editLabel,
      showHelper,
      isHelpActive,
      onHelpClick,
      helpEnabledFields,
      fieldKey,
      fieldId,
      getIsHelpActive,
      disabled = false,
      showComment,
      onCommentClick,
      unreadCommentCount = 0,
      customCommentIconVariant,
      isCommentActive,
      totalCommentCount = 0,
      testId,
    } = this.props;

    const commentIconIsActivated = !_.isEmpty(customCommentIconVariant)
      ? customCommentIconVariant
      : isCommentActive
      ? true
      : false;

    const { helpText } = _.find(helpEnabledFields, { uid: fieldKey }) || {};

    return (
      <LabelComponent
        isFieldHeader
        label={label}
        totalCommentCount={totalCommentCount}
        showComment={showComment}
        unreadCommentCount={unreadCommentCount}
        onCommentClick={() => onCommentClick({ id: fieldId, uid: fieldKey })}
        isCommentActive={commentIconIsActivated}
        testId={testId}
        showHelper={showHelper}
        isHelpActive={getIsHelpActive({ fielduid: fieldKey })}
        onHelpClick={() =>
          onHelpClick({
            fieldKey,
            helperTextId: helpText.id,
            id: fieldId,
          })
        }
      />
    );
  };

  renderEdit = () => {
    const {
      value,
      modalTitleLabel,
      setToastMsg,
      t,
      modalTitleEditLabel,
      disabled = false,
      editLabel,
      testId,
    } = this.props;
    const {
      showModal,
      currentDialogAction,
      showMediaModal,
      currentObjectDetails,
    } = this.state;

    const titleTextClass = classNames({
      [classes.titleText]: true,
      "text-label": true,
    });

    const remarkTextClass = classNames({
      [classes.remarkText]: true,
      ["text-body"]: true,
    });

    const sourceTextClass = classNames({
      [classes.sourceText]: true,
      "text-body": true,
    });

    return (
      <div className={classes.container}>
        {_.size(value) > 0 ? (
          <div className={classes.objectListContainer}>
            {_.map(value, object => (
              <div className={classes.objectItemContainer} key={object.id}>
                <div
                  className={classes.objectImage}
                  style={{ backgroundImage: `url(${object.imageUrl})` }}
                  onClick={() => this.showMediaModal(object)}
                  data-test-id={`${testId}-image-${object?.id}`}
                ></div>
                <div className={classes.objectInfoContainer}>
                  <div className={classes.textContainer}>
                    <div className={classes.titleRemarkContainer}>
                      <Tooltip
                        placement={"top"}
                        tooltip={
                          <div className={classes.tooltipText}>
                            {object?.title}
                          </div>
                        }
                      >
                        <span className={titleTextClass}>{object.title}</span>
                      </Tooltip>
                      <Tooltip
                        placement={"top"}
                        tooltip={
                          <div className={classes.tooltipText}>
                            {object?.remark}
                          </div>
                        }
                      >
                        <span className={remarkTextClass}>{object.remark}</span>
                      </Tooltip>
                    </div>
                    <div className={classes.actionsContainer}>
                      <IconButton
                        icon={<PencilLineOutlined variant="subtle" />}
                        type="plain"
                        variant="neutral"
                        isCompact
                        onClick={() => this.onEditClick(object)}
                        disabled={disabled}
                        testId={`${testId}-edit-${object?.id}`}
                      />
                      <IconButton
                        icon={<DeleteOutlined variant="subtle" />}
                        type="plain"
                        variant="neutral"
                        isCompact
                        onClick={() => this.onDeleteClick(object)}
                        disabled={disabled}
                        testId={`${testId}-delete-${object?.id}`}
                      />
                    </div>
                  </div>
                  {object.source && (
                    <Tooltip
                      placement={"top"}
                      tooltip={
                        <div className={classes.tooltipText}>
                          {t("common:source_colon_with_label", {
                            label: object.source,
                          })}
                        </div>
                      }
                    >
                      <span className={sourceTextClass}>
                        {t("common:source_colon_with_label", {
                          label: object.source,
                        })}
                      </span>
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : null}
        <span>
          <Button
            onClick={this.toggleModal}
            disabled={disabled}
            type="outlined"
            variant="neutral"
            icon={<AddOutlined />}
            testId={`${testId}-add`}
          >
            {editLabel}
          </Button>
        </span>

        {showModal && (
          <ModalComponent
            value={currentObjectDetails}
            modalTitleLabel={modalTitleLabel}
            modalTitleEditLabel={modalTitleEditLabel}
            onClose={this.toggleModal}
            updateObjectList={this.updateObjectList}
            setToastMsg={setToastMsg}
            testId={testId}
          />
        )}

        {currentDialogAction ? (
          <Dialog
            title={t(DIALOGS_INFO[currentDialogAction].title, {
              label: t(DIALOGS_INFO[currentDialogAction].label),
            })}
            onClose={this.closeDialogue}
            buttonOneText={t(
              DIALOGS_INFO[currentDialogAction].cancelButtonText
            )}
            buttonTwoText={t(
              DIALOGS_INFO[currentDialogAction].confirmButtonText
            )}
            onButtonOneClick={this.closeDialogue}
            onButtonTwoClick={this.onDelete}
            message={t(DIALOGS_INFO[currentDialogAction].message, {
              label: t(DIALOGS_INFO[currentDialogAction].label),
            })}
            dsVersion="2.0"
            testId={`${testId}-delete`}
          />
        ) : null}

        {showMediaModal && (
          <MediaModal
            toggleMediaModal={this.toggleMediaModal}
            attachments={[
              { url: currentObjectDetails.imageUrl, type: "IMAGE" },
            ]}
            testId={testId}
          />
        )}
      </div>
    );
  };
}

TOKObject.defaultProps = {
  ...UIBaseComponent.defaultProps,
};

const mapActionCreators = { setToastMsg };

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(null, mapActionCreators)
)(TOKObject);
